import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, ChevronDown, Search, X } from "lucide-react";

interface YearPickerProps {
  value?: string | number;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  helperText?: string;
  minYear?: number;
  maxYear?: number;
  className?: string;
  error?: string;
  disabled?: boolean;
  /** @deprecated não tem mais efeito — o seletor de décadas foi removido */
  showDecadeSelector?: boolean;
  allowClear?: boolean;
}

export function YearPicker({
  value,
  onChange,
  label = "Ano",
  required = false,
  helperText,
  minYear = 1990,
  maxYear = new Date().getFullYear() + 1,
  className,
  error,
  disabled,
  allowClear = true,
}: YearPickerProps) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const selectedYearRef = useRef<HTMLButtonElement>(null);

  const selectedYear = value ? Number(value) : null;
  const currentYear = new Date().getFullYear();

  // Todos os anos disponíveis, do mais recente para o mais antigo
  const allYears = useMemo(() => {
    const years = [];
    for (let y = maxYear; y >= minYear; y--) {
      years.push(y);
    }
    return years;
  }, [minYear, maxYear]);

  // Filtra anos por busca
  const filteredYears = useMemo(() => {
    if (!searchInput) return allYears;
    return allYears.filter((y) => y.toString().includes(searchInput));
  }, [allYears, searchInput]);

  // Ano digitado (4 dígitos) e se está dentro do intervalo permitido
  const typedYear = searchInput.length === 4 ? Number(searchInput) : null;
  const typedOutOfRange = typedYear !== null && (typedYear < minYear || typedYear > maxYear);

  // Ao abrir, rola até o ano selecionado (ou o atual) para deixá-lo visível
  useEffect(() => {
    if (open) {
      selectedYearRef.current?.scrollIntoView({ block: "center" });
    }
  }, [open]);

  const handleYearSelect = useCallback(
    (year: number) => {
      onChange(String(year));
      setOpen(false);
      setSearchInput("");
    },
    [onChange],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // aceita apenas dígitos, no máximo 4 (um ano)
    setSearchInput(e.target.value.replace(/\D/g, "").slice(0, 4));
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && typedYear !== null && !typedOutOfRange) {
      e.preventDefault();
      handleYearSelect(typedYear);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearchInput("");
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </label>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          className={buttonVariants({
            variant: "outline",
            className: cn(
              "min-h-10 justify-between bg-input px-3 font-normal",
              selectedYear && "font-medium text-foreground",
              error && "border-destructive focus:ring-destructive/20",
              "bg-transparent text-foreground placeholder:text-muted-foreground",
              "dark:bg-input/30",
            ),
          })}
          disabled={disabled}
        >
          <span className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 opacity-60" />
            <span className={selectedYear ? "text-foreground" : "text-muted-foreground"}>
              {selectedYear || "Selecione o ano"}
            </span>
          </span>

          <div className="flex items-center gap-1">
            {selectedYear && allowClear && (
              <X
                className="z-10 h-4 w-4 transition-colors hover:text-destructive"
                onClick={handleClear}
              />
            )}

            <ChevronDown
              className={cn("h-4 w-4 opacity-50 transition-transform", open && "rotate-180")}
            />
          </div>
        </PopoverTrigger>

        <PopoverContent className="pointer-events-auto w-72 overflow-hidden p-0" align="start">
          {/* Digite ou busque um ano */}
          <div className="border-b border-border p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Digite um ano..."
                value={searchInput}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                inputMode="numeric"
                autoFocus
                aria-invalid={typedOutOfRange}
                className={cn(
                  "min-h-10 pl-9 text-sm",
                  typedOutOfRange && "border-destructive focus-visible:ring-destructive/20",
                )}
              />
            </div>
            {typedOutOfRange ? (
              <p className="mt-1.5 text-xs text-destructive">
                Escolha um ano entre {minYear} e {maxYear}.
              </p>
            ) : (
              <p className="mt-1.5 text-xs text-muted-foreground">Enter confirma o ano digitado.</p>
            )}
          </div>

          {/* Atalhos para os anos mais usados */}
          <div className="flex gap-2 px-3 pt-3">
            <Button
              variant={selectedYear === currentYear ? "default" : "outline"}
              size="sm"
              className="h-9 flex-1 text-xs"
              onClick={() => handleYearSelect(currentYear)}
            >
              Este ano ({currentYear})
            </Button>
            <Button
              variant={selectedYear === currentYear - 1 ? "default" : "outline"}
              size="sm"
              className="h-9 flex-1 text-xs"
              onClick={() => handleYearSelect(currentYear - 1)}
            >
              Ano passado ({currentYear - 1})
            </Button>
          </div>

          {/* Lista de anos, do mais recente ao mais antigo */}
          <p className="px-3 pb-1 pt-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Todos os anos
          </p>
          <div className="grid max-h-44 grid-cols-4 gap-1 overflow-y-auto px-3 pb-3">
            {filteredYears.length > 0 ? (
              filteredYears.map((year) => (
                <Button
                  key={year}
                  ref={year === (selectedYear ?? currentYear) ? selectedYearRef : undefined}
                  variant={selectedYear === year ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-8 text-xs font-medium",
                    year === currentYear && selectedYear !== year && "font-semibold text-primary",
                  )}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </Button>
              ))
            ) : (
              <p className="col-span-4 py-4 text-center text-sm text-muted-foreground">
                Nenhum ano encontrado
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {error ? (
        <span className="text-xs text-destructive">{error}</span>
      ) : (
        helperText && <p className="text-xs text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
}
